import { cmp, getConsentFor, getCookie, isString } from '@guardian/libs';
import { supportsPerformanceAPI } from '../event-timer';
import { getLocale } from '../geo/get-locale';
import { getContentTargeting } from './content';
import { getPersonalisedTargeting } from './personalised';
import { getSessionTargeting } from './session';
import { getLocalHour, getSharedTargeting } from './shared';
import { getViewportTargeting } from './viewport';
const filterValues = (pageTargets) => {
    const filtered = {};
    for (const key in pageTargets) {
        const value = pageTargets[key];
        if (isString(value)) {
            filtered[key] = value;
        }
        else if (Array.isArray(value) &&
            value.length > 0 &&
            value.every(isString)) {
            filtered[key] = value;
        }
    }
    return filtered;
};
const lastPerformanceEntryIsNavigationType = () => {
    if (!supportsPerformanceAPI()) {
        return false;
    }
    const navigationEvents = performance.getEntriesByType('navigation');
    const lastNavigationEvent = navigationEvents[navigationEvents.length - 1];
    // https://developer.mozilla.org/en-US/docs/Web/API/PerformanceEntry/entryType#navigation
    return lastNavigationEvent?.entryType === 'navigation';
};
const referrerMatchesHost = (referrer) => {
    if (!referrer) {
        return false;
    }
    const referrerUrl = new URL(referrer);
    return referrerUrl.hostname === window.location.hostname;
};
// A consentless friendly way of determining if this is the users first visit to the page
const isFirstVisit = (referrer) => {
    if (supportsPerformanceAPI() && !lastPerformanceEntryIsNavigationType()) {
        return false;
    }
    return !referrerMatchesHost(referrer);
};
const buildPageTargeting = ({ adFree, clientSideParticipations, consentState, isSignedIn = false, youtube = false, idProviders = [], }) => {
    const { page, isDotcomRendering } = window.guardian.config;
    const adFreeTargeting = adFree ? { af: 't' } : {};
    const sharedAdTargeting = page.sharedAdTargeting
        ? getSharedTargeting(page.sharedAdTargeting)
        : {};
    const contentTargeting = getContentTargeting({
        webPublicationDate: page.webPublicationDate,
        eligibleForDCR: page.dcrCouldRender,
        path: `/${page.pageId}`,
        renderingPlatform: isDotcomRendering
            ? 'dotcom-rendering'
            : 'dotcom-platform',
        section: page.section,
        sensitive: page.isSensitive,
        videoLength: page.videoDuration,
        keywords: sharedAdTargeting.k ?? [],
    });
    const referrer = document.referrer || '';
    const sessionTargeting = getSessionTargeting({
        adTest: getCookie({ name: 'adtest', shouldMemoize: true }),
        countryCode: getLocale(),
        localHour: getLocalHour(),
        isSignedIn,
        pageViewId: window.guardian.config.ophan.pageViewId,
        participations: {
            clientSideParticipations,
            serverSideParticipations: window.guardian.config.tests ?? {},
            betaAbTestParticipations: window.guardian.modules.abTests?.getParticipations() ?? {},
        },
        referrer,
        idProviders,
    });
    const getViewport = () => {
        return {
            width: window.innerWidth || document.body.clientWidth || 0,
            height: window.innerHeight || document.body.clientHeight || 0,
        };
    };
    const viewportTargeting = getViewportTargeting({
        viewPortWidth: getViewport().width,
        cmpBannerWillShow: !cmp.hasInitialised() || cmp.willShowPrivacyMessageSync(),
    });
    const personalisedTargeting = getPersonalisedTargeting({
        state: consentState,
        youtube,
    });
    const consentlessTargeting = {};
    if (!getConsentFor('googletag', consentState)) {
        consentlessTargeting.firstvisit = isFirstVisit(referrer) ? 't' : 'f';
    }
    const pageTargets = {
        ...personalisedTargeting,
        ...sharedAdTargeting,
        ...adFreeTargeting,
        ...contentTargeting,
        ...sessionTargeting,
        ...viewportTargeting,
        ...consentlessTargeting,
    };
    // filter !(string | string[]) and empty values
    const pageTargeting = filterValues(pageTargets);
    return pageTargeting;
};
export { buildPageTargeting, filterValues, getLocalHour };
